import React from 'react';
import { useTranslation } from 'react-i18next';
import translations from 'src/internationalisation/translation-map.json';
import { probabilities } from 'src/enums/simulations';
import { BarChart, LineChart } from '@oliasoft-open-source/charts-library';
import { getDurationOrCost, getMetersPerDay } from 'store/entities/simulations/simulations';
import { displayNumber, round, unum } from '@oliasoft-open-source/units';
import { SimulationChartType } from 'src/enums/campaigns';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { capitalize } from 'lodash';
import { HOURS_PER_DAY } from 'src/enums/general';
import { generateCumulativeChartData, shownCumulativeProbabilitiesForChart } from 'src/views/simulations/simulations';
import { formatToMillions } from 'client/common/format-data/format-to-millions';
import { getLearningCurveAdjustedValue } from 'src/views/campaigns/utils/utils';

const RawSummaryChart = ({
  campaign,
  probability,
  dataEntity,
  primaryCurrency,
  chartType,
  controlsPortalId,
  depthUnit = { depthUnit },
}) => {
  const { t } = useTranslation();

  const formatCostData = (chartData) => {
    const newChartData = structuredClone(chartData);

    newChartData.datasets = newChartData.datasets.map((dataset) => {
      const formattedDataArray = dataset.data.map((item) => {
        if (Array.isArray(item)) {
          return item.map((num) => formatToMillions(num));
        }
        return formatToMillions(item);
      });

      return {
        ...dataset,
        data: formattedDataArray,
      };
    });

    return newChartData;
  };

  const formatProbabilityString = () => {
    const prefix = probability === probabilities.MEAN ? '' : 'P';
    const capitalizedProbability = `${prefix}${capitalize(probability)}`;

    if (dataEntity === 0) {
      return `${capitalizedProbability} ${t(translations.days)}`;
    } else {
      return `${capitalizedProbability} ${t(translations.cost)} [M${primaryCurrency}]`;
    }
  };

  const yLabel =
    chartType === SimulationChartType.METERS_PER_DAY
      ? t(depthUnit === 'm' ? translations.campaigns_metersPerDay : translations.campaigns_feetPerDay)
      : formatProbabilityString();

  const options = {
    chartStyling: {
      width: '80%',
    },
    legend: {
      position: 'top-left',
    },
    axes: {
      y: [
        {
          label: yLabel,
        },
      ],
    },
    additionalAxesOptions: {
      stackedX: true,
    },
    tooltip: {
      scientificNotation: false,
    },
    graph: {
      showDataLabels: true,
    },
  };

  const labels = [];
  const adjustedDuration = [];
  const datasetData = [];
  const backgroundColor = [];
  const learningCurveData = [];
  let sumOfLearningCurveData = 0;
  let content;

  let totalAdjusted = 0;

  const calculateLearningCurveData = (adjustedDuration, duration, index, learningCurveData) => {
    const adjustedDurationLength = adjustedDuration.map(([first, second]) => second - first);
    const learningCurveValue = duration - adjustedDurationLength[index];
    learningCurveData.push(learningCurveValue);

    sumOfLearningCurveData = learningCurveData.reduce((acc, val) => acc + val, 0);

    return learningCurveData;
  };

  const handleOnHover = (ev, di, i, gd) => {
    const { chart } = ev;
    const { x, y } = gd[di].data[i];
    const value = t(dataEntity === 0 ? `${t(translations.days)}` : `${primaryCurrency}`);

    const tooltipText = `P${y}: ${displayNumber(round(x, 2), {
      scientific: false,
    })} ${value}`;
    chart.config.options.plugins.tooltip.callbacks.label = (ctx) => {
      let label = ctx.dataset.label || '';
      if (label) {
        label = tooltipText;
      }
      if (
        di === 0 &&
        i === 0 &&
        dataEntity === 0 &&
        gd?.length > 1 &&
        ctx.dataset.label === t(translations.simulations_cumulativeProbability)
      ) {
        label = '';
      }
      return label;
    };

    chart.config.options.plugins.tooltip.callbacks.title = (ctx) => {
      let label = ctx[0].label || '';
      if (label) {
        label = '';
      }
      return label;
    };
  };

  const label = Object.keys(probabilities).find((key) => probabilities[key] === probability);

  const data = {
    labels,
    datasets: [
      {
        data: datasetData,
        label,
        backgroundColor,
        borderColor: backgroundColor[0],
        barPercentage: 0.85,
      },
      ...(campaign.learningCurve
        ? [
            {
              data: adjustedDuration,
              label: `Adjusted ${label}`,
              backgroundColor: 'hsla(60, 20%, 50%, 0.85)',
              borderColor: 'hsla(60, 20%, 50%, 0.85)',
              barPercentage: 1,
            },
          ]
        : []),
    ],
  };

  switch (chartType) {
    case SimulationChartType.TOTAL_DAYS: {
      campaign?.estimates?.reduce((acc, curr, index) => {
        const previousValue = index === 0 ? 0 : acc[index - 1]?.[1];
        const duration = getDurationOrCost(curr.simulation, probability, dataEntity) ?? 0;
        labels.push(curr.estimateName);
        if (campaign.learningCurve) {
          const adjustedValueForEstimate = getLearningCurveAdjustedValue(
            curr.simulation,
            duration,
            curr.adjustment,
            dataEntity,
            probability,
          );
          const adjustedValue = previousValue + adjustedValueForEstimate;

          adjustedDuration.push([previousValue, adjustedValue]);

          calculateLearningCurveData(adjustedDuration, duration, index, learningCurveData);
        }
        const newValue = round(previousValue + duration, 2);
        if (newValue > totalAdjusted) {
          totalAdjusted = newValue;
        }
        backgroundColor.push('hsl(0, 0%, 83%)');

        acc.push([previousValue, newValue]);
        return acc;
      }, datasetData);
      if (datasetData.length > 0) {
        datasetData.push(totalAdjusted);
        backgroundColor.push('hsl(60, 32%, 65%)');
        labels.push(t(translations.campaigns_cumulativeTotal));
        if (campaign.learningCurve) {
          const sumOfAdjustedDuration = adjustedDuration.reduce(
            (sum, subArray) => sum + (subArray[1] - subArray[0]),
            0,
          );
          adjustedDuration.push([0, sumOfAdjustedDuration]);
        }
      }

      if (campaign?.campaignTotal?.simulation) {
        const totalDuration = getDurationOrCost(campaign?.campaignTotal?.simulation, probability, dataEntity) ?? 0;
        datasetData.push(totalDuration);
        backgroundColor.push('hsl(61, 33%, 70%)');
        labels.push(t(translations.campaigns_campaignTotal));
        if (campaign.learningCurve) {
          adjustedDuration.push([0, totalDuration - sumOfLearningCurveData]);
        }
      }

      const formattedData = dataEntity === 1 ? formatCostData(data) : data;
      content = <BarChart chart={{ data: formattedData, options, controlsPortalId }} />;
      break;
    }
    case SimulationChartType.METERS_PER_DAY: {
      campaign.estimates.forEach((estimate) => {
        const duration = getDurationOrCost(estimate.simulation, probability, dataEntity) ?? 0;
        const metersPerDay = getMetersPerDay(estimate.simulation, duration, probability) ?? 0;
        backgroundColor.push('hsla(61, 33%, 60%, 0.85)');

        labels.push(estimate.estimateName);
        if (campaign.learningCurve) {
          const adjustedEstimateDuration = round(duration * (1 - estimate.adjustment / 100), 3);
          const adjustedMetersPerDay = getMetersPerDay(estimate.simulation, adjustedEstimateDuration, probability) ?? 0;

          adjustedDuration.push(unum(adjustedMetersPerDay, depthUnit, 'm'));
        }

        datasetData.push(unum(metersPerDay, depthUnit, 'm'));
      });
      content = <BarChart chart={{ data, options, controlsPortalId }} />;
      break;
    }
    case SimulationChartType.CUMULATIVE_PROBABILITY: {
      const campaignTotalSimulation = campaign?.campaignTotal?.simulation;
      const probabilities =
        dataEntity === 0
          ? campaignTotalSimulation?.cumulative_probability?.probabilities
          : campaignTotalSimulation?.cumulative_probability_cost?.probabilities;
      const time = campaignTotalSimulation?.cumulative_probability?.time?.map((t) => t / HOURS_PER_DAY);
      const subactivity = campaignTotalSimulation?.cumulative_subactivity_percentiles?.map((el) => el / HOURS_PER_DAY);
      const cost =
        campaignTotalSimulation?.cumulative_probability_cost?.costs ??
        campaignTotalSimulation?.cumulative_probability_cost?.cost;
      const chartDataByEntity = dataEntity === 0 ? time : cost;
      const adjustedChartDataByEntity =
        campaign.learningCurve && chartDataByEntity
          ? chartDataByEntity.map((value, index) => {
              const percentile = index + 1;
              const learningCurveReduction = campaign.estimates.reduce((sum, estimate) => {
                const estimateValue = getDurationOrCost(estimate.simulation, `${percentile}`, dataEntity) ?? 0;
                const adjustedEstimateValue = getLearningCurveAdjustedValue(
                  estimate.simulation,
                  estimateValue,
                  estimate.adjustment,
                  dataEntity,
                  `${percentile}`,
                );

                return sum + (estimateValue - adjustedEstimateValue);
              }, 0);

              return value - learningCurveReduction;
            })
          : [];
      const hideLine = (ctx, value) => {
        return ctx.p0.parsed.y === ctx.p1.parsed.y ? value : undefined;
      };

      if (probabilities && probabilities.length > 0 && chartDataByEntity && chartDataByEntity.length > 0) {
        const chartData = generateCumulativeChartData(chartDataByEntity, shownCumulativeProbabilitiesForChart);
        const adjustedChartData = generateCumulativeChartData(
          adjustedChartDataByEntity,
          shownCumulativeProbabilitiesForChart,
        );
        const chartDataSubactivity = generateCumulativeChartData(subactivity, shownCumulativeProbabilitiesForChart);
        const chartOptions = {
          controlsPortalId,
          options: {
            chartStyling: {
              height: '70%',
            },
            additionalAxesOptions: {
              reverse: true,
            },
            axes: {
              x: [
                {
                  label:
                    dataEntity === 0
                      ? `${t(translations.time)} [${t(translations.days)}]`
                      : `${t(translations.cost)} [${primaryCurrency}]`,
                },
              ],
            },
            scales: {
              y: {
                ticks: {
                  callback: (val) => {
                    if (val >= 1 && val <= 99) {
                      return `P${val}`;
                    }
                    return '';
                  },
                },
              },
              x: {
                ticks: {
                  callback: (val) => {
                    return displayNumber(val, { scientific: false });
                  },
                },
              },
            },
            interactions: {
              onHover: handleOnHover,
            },
            tooltip: {
              scientificNotation: false,
            },
          },
          data: {
            datasets: [
              dataEntity === 0 && chartDataSubactivity?.length > 0
                ? {
                    label: t(translations.simulations_riskOnly),
                    data: chartDataSubactivity,
                    pointBackgroundColor: 'rgba(107, 156, 131)',
                    borderColor: 'rgba(107, 156, 131)',
                    backgroundColor: 'rgba(107, 156, 131, 0.3)',
                    fill: { value: 99 },
                  }
                : null,
              {
                label: t(translations.simulations_cumulativeProbability),
                data:
                  dataEntity === 0 && chartDataSubactivity?.length > 0
                    ? [
                        {
                          x: chartDataSubactivity?.[0].x,
                          y: 0 + 1,
                        },
                        ...chartData,
                      ]
                    : chartData,
                pointBackgroundColor: 'rgba(129, 129, 78)',
                borderColor: 'rgba(129, 129, 78)',
                backgroundColor: 'rgba(129, 129, 78, 0.3)',
                segment: {
                  borderColor: (ctx) => hideLine(ctx, 'rgba(129, 129, 78, 0'),
                },
                ...(dataEntity === 0 && {
                  fill: { value: 99 },
                }),
              },
              campaign.learningCurve && adjustedChartData?.length > 0
                ? {
                    label: `${t(translations.adjusted)} ${t(translations.simulations_cumulativeProbability)}`,
                    data: adjustedChartData,
                    pointBackgroundColor: 'rgba(94, 117, 181)',
                    borderColor: 'rgba(94, 117, 181)',
                    backgroundColor: 'rgba(94, 117, 181, 0.3)',
                    segment: {
                      borderColor: (ctx) => hideLine(ctx, 'rgba(94, 117, 181, 0'),
                    },
                  }
                : null,
            ].filter(Boolean),
          },
        };
        content = <LineChart chart={chartOptions} />;
      }
      break;
    }
    default: {
      break;
    }
  }

  return content ?? null;
};

export const SummaryChart = withErrorBoundary(RawSummaryChart);
